import React, { useState, useEffect } from "react";
import { FormControl, Select, MenuItem, Typography } from "@mui/material";

const SelectComponent = ({
  options,
  value: propsValue,
  onChange,
  isMultiSelect = false,
  labelStyle = {},
  selectStyle = {},
  selectLabel = "",
  placeholder = "",
  required = true,
  disabled = false,
  ...props
}) => {
  const [selectValue, setSelectValue] = useState(propsValue);
  const allOptionValue = "All";

  useEffect(() => {
    setSelectValue(propsValue);
  }, [propsValue]);

  const getClickedValueFromOptions = (options, currentValue) => {
    if (!Array.isArray(currentValue)) {
      return currentValue;
    }
    const clickedValue = options.find((option) => !currentValue.includes(option.value));
    return clickedValue ? clickedValue.value : "";
  };

  const handleSelectChange = (event) => {
    let newValue = event.target.value;
    if (isMultiSelect) {
      const isCurrentlyAllSelected = Array.isArray(selectValue) && selectValue.length === options.length;

      if (isCurrentlyAllSelected && !newValue.includes(allOptionValue)) {
        // When clicking on an option, uncheck all and only keep the currently clicked option
        const clickedValue = getClickedValueFromOptions(options, newValue);
        newValue = [clickedValue];
      } else {
        // If 'ALL' is selected, select all options
        if (newValue.includes(allOptionValue)) {
          newValue = options.map((option) => option.value);
        }
        // If 'ALL' is deselected and the list is not empty, remove 'ALL'
        else if (!newValue.includes(allOptionValue) && newValue.length > 0) {
          newValue = newValue.filter((value) => value !== allOptionValue);
        }
        // If the list is empty and it's required, reset to previous value
        else if (newValue.length === 0 && required) {
          newValue = selectValue;
        }
      }
    } else {
      if (!newValue && required) {
        newValue = selectValue;
      }
    }
    const selectedLabel = getSelectedLabels(newValue);
    setSelectValue(newValue);
    onChange(newValue, selectedLabel);
  };
  const getSelectedLabels = (values) => {
    if (typeof values === "string" || typeof values === "number") {
      const selectedItem = options.find((option) => option.value === values);
      return selectedItem ? selectedItem.label : "";
    } else if (Array.isArray(values)) {
      return values
        .map((value) => {
          const selectedItem = options.find((option) => option.value === value);
          return selectedItem ? selectedItem.label : "";
        })
        .filter((v) => v)
        .join(", ");
    }
    return "";
  };

  return (
    <>
      {selectLabel && (
        <Typography variant="subtitle1" sx={{ whiteSpace: "nowrap", color: "#333", fontWeight: 700, ...labelStyle }}>
          {selectLabel}
        </Typography>
      )}
      <FormControl variant="outlined" sx={{ my: 0.5, width: selectStyle?.width }}>
        <Select
          disabled={disabled}
          value={selectValue}
          onChange={handleSelectChange}
          multiple={isMultiSelect}
          displayEmpty
          renderValue={(selected) => {
            if (!selected || selected === "" || selected.length === 0) {
              return <div style={{ color: "#98a1a9" }}>{placeholder}</div>;
            }
            if (selected.length === options.length) {
              return "All";
            }
            return getSelectedLabels(selected);
          }}
          inputProps={{ "aria-label": "Without label", ...props.inputStyle }}
          required={required}
          MenuProps={{
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "left",
            },
            transformOrigin: {
              vertical: "top",
              horizontal: "left",
            },
          }}
          sx={{
            width: "140px",
            backgroundColor: "#fff",
            "& .MuiSelect-select": {
              py: 0.5,
              pl: 2,
            },
            ...selectStyle,
          }}
        >
          {isMultiSelect && (
            <MenuItem key={allOptionValue} value={allOptionValue} sx={{ minHeight: 20 }}>
              All
            </MenuItem>
          )}
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value} sx={{ minHeight: 20 }}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </>
  );
};

export default SelectComponent;
